/**
 * Layout админки: десктоп — sidebar Navbar, мобайл — Burger + Drawer.
 *
 * Защита: рендерит детей только если useMe().data.isAdmin === true.
 * Иначе показывает 403-alert (не редирект — пользователь может видеть
 * причину, а не «куда меня кинуло»).
 */

import {
    Alert, AppShell, Burger, Center, Group, Loader, NavLink, ScrollArea, Text, Title,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { Link, NavLink as RouterNavLink, Outlet, useLocation } from 'react-router-dom';
import { useMe } from '../../hooks/use-me';
import { DESKTOP_QUERY } from '../../lib/breakpoints';

const NAV_ITEMS: Array<{ to: string; label: string; end?: boolean }> = [
    { to: '/admin',                 label: 'Обзор',             end: true },
    { to: '/admin/users',           label: 'Пользователи' },
    { to: '/admin/chats',           label: 'Чаты' },
    { to: '/admin/tools',           label: 'Тулы' },
    { to: '/admin/usage',           label: 'Токены' },
    { to: '/admin/cache',           label: 'Кэш' },
    { to: '/admin/chat-settings',   label: 'Настройки чата' },
    { to: '/admin/admins',          label: 'Админы' },
];

export function AdminLayout(): JSX.Element {
    const me = useMe();
    const [opened, { toggle, close }] = useDisclosure(false);
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    const location = useLocation();

    if (me.isLoading) {
        return <Center mih="60vh"><Loader /></Center>;
    }
    if (!me.data?.user) {
        return (
            <Center mih="60vh" px="md">
                <Alert color="yellow" title="Нужен вход" maw={420}>
                    Эта страница доступна только админам. <Link to="/">На главную</Link>, войти через GitHub.
                </Alert>
            </Center>
        );
    }
    if (!me.data.isAdmin) {
        return (
            <Center mih="60vh" px="md">
                <Alert color="red" title="403 — Нет доступа" maw={420}>
                    У вашей учётной записи нет прав администратора.
                </Alert>
            </Center>
        );
    }

    return (
        <AppShell
            header={{ height: 56 }}
            navbar={{
                width:     220,
                breakpoint: 'lg',
                collapsed: { mobile: !opened, desktop: false },
            }}
            padding={isDesktop ? 'lg' : 'sm'}
        >
            <AppShell.Header>
                <Group h="100%" px="md" justify="space-between">
                    <Group gap="sm">
                        <Burger opened={opened} onClick={toggle} hiddenFrom="lg" size="sm" />
                        <Link to="/admin" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Title order={4}>pflow · admin</Title>
                        </Link>
                    </Group>
                    <Text fz="sm" c="dimmed" visibleFrom="sm">{me.data.user.email}</Text>
                </Group>
            </AppShell.Header>
            <AppShell.Navbar p="xs">
                <ScrollArea>
                    {NAV_ITEMS.map(item => (
                        <NavLink
                            key={item.to}
                            label={item.label}
                            component={RouterNavLink}
                            to={item.to}
                            end={item.end}
                            active={item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)}
                            onClick={() => { if (!isDesktop) close(); }}
                        />
                    ))}
                    <NavLink
                        label="← На главную"
                        component={Link}
                        to="/"
                        c="dimmed"
                        mt="md"
                    />
                </ScrollArea>
            </AppShell.Navbar>
            <AppShell.Main>
                <Outlet />
            </AppShell.Main>
        </AppShell>
    );
}
